import { Vessel, PartInstance } from '../core/vessel/vessel'
import { PartCatalog } from '../core/vessel/catalog'
import { Design } from './design'

const VERSIONE = 1
const PREFISSO = 'apogeo:progetto:'

interface FileProgetto {
  versione: number
  nome: string
  salvatoIl: string
  rootId: string
  parts: PartInstance[]
  symmetryGroups: Record<string, string[]>
}

export function serializza(design: Design, quando: string): string {
  const file: FileProgetto = {
    versione: VERSIONE,
    nome: design.nome,
    salvatoIl: quando,
    rootId: design.vessel.rootId,
    parts: design.vessel.parts,
    symmetryGroups: design.symmetryGroups,
  }
  return JSON.stringify(file, null, 2)
}

/**
 * Rilegge un progetto, controllando che sia sensato.
 *
 * Un file rotto deve dare un errore leggibile, non un razzo mostruoso che poi
 * esplode dentro la fisica con un NaN: e molto piu facile capire "parte
 * sconosciuta: motore-atomico" che "il razzo e sparito".
 */
export function deserializza(testo: string, catalog: PartCatalog): Design {
  let file: FileProgetto
  try {
    file = JSON.parse(testo) as FileProgetto
  } catch {
    throw new Error('Il file non è un progetto valido: non è nemmeno JSON.')
  }

  if (typeof file !== 'object' || file === null) throw new Error('Il file non contiene un progetto.')
  if (file.versione !== VERSIONE) {
    throw new Error(`Versione del progetto non supportata: ${file.versione} (questa è la ${VERSIONE}).`)
  }
  if (!Array.isArray(file.parts) || file.parts.length === 0) {
    throw new Error('Il progetto non contiene nessuna parte.')
  }

  const ids = new Set<string>()
  for (const p of file.parts) {
    if (!catalog[p.defId]) throw new Error(`Parte sconosciuta nel progetto: ${p.defId}`)
    if (ids.has(p.id)) throw new Error(`Due parti con lo stesso identificativo: ${p.id}`)
    ids.add(p.id)
    for (const v of [p.position, p.rotation]) {
      if (!v || Object.values(v).some((n) => !Number.isFinite(n))) {
        throw new Error(`Coordinate non valide sulla parte ${p.id}`)
      }
    }
  }
  for (const p of file.parts) {
    if (p.parentId !== null && !ids.has(p.parentId)) {
      throw new Error(`La parte ${p.id} è appesa a un pezzo che non esiste: ${p.parentId}`)
    }
  }
  if (!ids.has(file.rootId)) throw new Error('La radice del progetto non esiste fra le parti.')

  const vessel: Vessel = { rootId: file.rootId, parts: file.parts }
  return { nome: file.nome ?? 'Senza nome', vessel, symmetryGroups: file.symmetryGroups ?? {} }
}

/** localStorage esiste solo nel browser: in Node il modulo non deve esplodere. */
function magazzino(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function salvaLocale(design: Design, quando: string): boolean {
  const m = magazzino()
  if (!m) return false
  m.setItem(PREFISSO + design.nome, serializza(design, quando))
  return true
}

export function caricaLocale(nome: string, catalog: PartCatalog): Design | null {
  const m = magazzino()
  const testo = m?.getItem(PREFISSO + nome)
  return testo ? deserializza(testo, catalog) : null
}

export interface VoceElenco { nome: string; salvatoIl: string }

/** I progetti salvati, dal piu recente. */
export function elencoLocale(): VoceElenco[] {
  const m = magazzino()
  if (!m) return []
  const voci: VoceElenco[] = []
  for (let i = 0; i < m.length; i++) {
    const chiave = m.key(i)
    if (!chiave?.startsWith(PREFISSO)) continue
    try {
      const f = JSON.parse(m.getItem(chiave)!) as FileProgetto
      voci.push({ nome: f.nome, salvatoIl: f.salvatoIl })
    } catch {
      // una voce illeggibile non deve nascondere le altre
    }
  }
  return voci.sort((a, b) => b.salvatoIl.localeCompare(a.salvatoIl))
}

export function cancellaLocale(nome: string): void {
  magazzino()?.removeItem(PREFISSO + nome)
}
